<script setup lang="ts">
import type { OnActiveItem, UiItem } from './ProductSelectItems.vue'

export type Props = {
  product: Pick<ProductListingFragmentFull, 'id' | '_values' | 'sellerId'> | None
  onActive?: OnActiveItem | None
  onImage?: (img: gen.ImageNodeItemFragment | None) => void
}

const t2 = useT2({
  'Size guide': 'Dydžių gidas',
})

const props = defineProps<Props>()

type UiItem2 = UiItem & { parentIndex: number; boximage: gen.ImageNodeItemFragment | None }

const variantsList = computed(() => {
  const productMain = props.product
  const currentId = productMain?.id

  return helpers.arr(productMain?._values?.options, (doc, parentIndex) => {
    const isColor = doc.values.some((i) => helpers.isDef(i.hsla?.h))
    const isImage = doc.values.some((i) => i.image)

    return {
      doc,
      variantList: doc.values.map((val, index): UiItem2 => {
        const product = val.product
        return {
          key: val.product?.productId || index.toString(),
          productId: product?.productId,
          title: helpers.joinStringCb(' - ', [product?.title, val.name], (i) => i),
          img: val.image,
          isCurrent: currentId === product?.productId,
          name: val.name || '',
          isImage,
          isColor,
          color: val.hsla ? toColorHsla(val.hsla) : '',
          inStock: !!product?.inStock,
          slug: val?.product?.slug,
          parentIndex,
          boximage: product?.image,
        }
      }),
    }
  })
})

const data = shallowReactive({
  elem: undefined as undefined | HTMLElement,
  sizeGuide: undefined as undefined | string,
  labelHoverHolder: undefined as { label: string; index: number } | undefined,
})

const labels = computed(() => {
  const hover = data.labelHoverHolder
  return variantsList.value.map((item, index) => {
    if (hover?.index === index) return hover?.label
    return item.variantList.find((i) => i.isCurrent)?.name || item.doc.name
  })
})

function mouseleave() {
  data.labelHoverHolder = undefined
  props.onImage?.(undefined)
}
function mouseenter(item: UiItem2) {
  if (item.boximage || item.isCurrent) props.onImage?.(item.boximage)
  data.labelHoverHolder = { label: item.name, index: item.parentIndex }
}
function sizeGuideOpen(e: unknown | MouseEvent, key: string) {
  data.elem = e instanceof MouseEvent ? helpers.getHTMLElement(e.target) : undefined
  data.sizeGuide = data.sizeGuide === key ? undefined : key
}
function sizeGuideClose() {
  scrollToElem(data.elem, { behavior: 'smooth', offsetTop: 100, tolarance: 200 })
  window.setTimeout(() => (data.sizeGuide = undefined), 100)
}
const mobile = useMobile()
</script>

<template>
  <div v-if="variantsList.length > 0">
    <div v-for="(item, index) in variantsList" :key="item.doc.key" :class="variantsList.length !== index + 1 && '_pb4'">
      <ProductSelectHeader :name="item.doc.name" :value="labels[index] || ''" :seller-id="product?.sellerId">
        <template v-if="item.doc.guideBodyId || item.doc.guideProductId">
          <ButtonInline v-if="mobile" blue :txt="undefined" :open="data.sizeGuide === item.doc.key" @click.passive="(e: unknown) => sizeGuideOpen(e, item.doc.key)">
            <IconInfo class="_svg" />
            <span class="_fs08 _ml06">{{ t2('Size guide') }} </span>
          </ButtonInline>
          <LazySizeGuideModal v-else :seller-id="product?.sellerId" :guide-body-id="item.doc.guideBodyId" :guide-product-id="item.doc.guideProductId" />
        </template>
      </ProductSelectHeader>

      <ProductSelectItems :items="item.variantList" :mouseleave :mouseenter :on-active />

      <UiGrow v-if="mobile && (item.doc.guideBodyId || item.doc.guideProductId)" :open="data.sizeGuide === item.doc.key">
        <LazySizeGuide4Mobile :seller-id="product?.sellerId" :guide-body-id="item.doc.guideBodyId" :guide-product-id="item.doc.guideProductId" :exit="() => sizeGuideClose()" />
      </UiGrow>
    </div>
  </div>
</template>
